// ✅ src/pages/VehicleV2/ProductionAndDieselConsReport.jsx
// FULL UPDATED FILE
// Backend:
//   app.use("/api/productionDieselConsReport", productionDieselConsReport);

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/productionDieselConsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMYYYY = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const parseDDMMYYYY = (s) => {
  const v = toStr(s);
  if (!v) return null;

  const [ddS, mmS, yyyyS] = v.split("/");
  const dd = Number(ddS);
  const mm = Number(mmS);
  const yyyy = Number(yyyyS);

  if (!dd || !mm || !yyyy) return null;

  const dt = new Date(yyyy, mm - 1, dd);
  if (
    dt.getFullYear() !== yyyy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  ) {
    return null;
  }

  dt.setHours(0, 0, 0, 0);
  return dt;
};

const today0 = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

const compareDateOnly = (a, b) => {
  if (!a || !b) return 0;
  const aa = new Date(a);
  const bb = new Date(b);
  aa.setHours(0, 0, 0, 0);
  bb.setHours(0, 0, 0, 0);
  return aa.getTime() === bb.getTime() ? 0 : aa.getTime() < bb.getTime() ? -1 : 1;
};

export default function ProductionAndDieselConsReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // optional existing value
  const financeYearStart = toStr(
    localStorage.getItem("financeyearstartdate") || ""
  );

  // ✅ backend matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branches, setBranches] = useState([{ label: "Select", value: "" }]);

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    branch: branchType === "HO" ? "" : branchId,
    fromDate: "",
    toDate: "",
    vehtype: toStr(localStorage.getItem("prvehtype") || "O"),
    vehCode: "",
    vehName: "",
    vehRegNo: "",
  });

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage]);

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage) || 1;

  const validate = (useFilters = filters) => {
    const fd = parseDDMMYYYY(useFilters.fromDate);
    const td = parseDDMMYYYY(useFilters.toDate);
    const fy = parseDDMMYYYY(financeYearStart);

    if (useFilters.fromDate && !fd) {
      toast.error("Invalid From Date (use dd/MM/yyyy)");
      return false;
    }

    if (useFilters.toDate && !td) {
      toast.error("Invalid To Date (use dd/MM/yyyy)");
      return false;
    }

    if (fd && fd.getTime() > today0().getTime()) {
      toast.error("Select from date lesser than current date");
      return false;
    }

    if (td && td.getTime() > today0().getTime()) {
      toast.error("Select to date lesser than current date");
      return false;
    }

    if (fd && td && compareDateOnly(td, fd) < 0) {
      toast.error("Please select from date less than to date");
      return false;
    }

    if (fy) {
      if (fd && compareDateOnly(fd, fy) < 0) {
        toast.error(`You can select only >= Financial Year Date ${financeYearStart}`);
        return false;
      }
      if (td && compareDateOnly(td, fy) < 0) {
        toast.error(`You can select only >= Financial Year Date ${financeYearStart}`);
        return false;
      }
    }

    return true;
  };

  const fetchInit = async () => {
    const params = {
      branchType,
      branchId,
      userId,
    };

    const { data } = await axios.get(`${API_BASE}/init`, {
      params,
      headers: apiHeaders,
    });

    if (data?.success === false) {
      throw new Error(data?.message || "Init failed");
    }

    setBranches(data?.branches || [{ label: "Select", value: "" }]);

    if (data?.defaults) {
      setFilters((prev) => ({
        ...prev,
        fromDate: prev.fromDate || data.defaults.fromDate || "",
        toDate: prev.toDate || data.defaults.toDate || "",
      }));
    }
  };

  const fetchList = async (useFilters = filters) => {
    if (!validate(useFilters)) return;

    const params = {
      branchType,
      branch: useFilters.branch,
      fromDate: useFilters.fromDate,
      toDate: useFilters.toDate,
      vehtype: useFilters.vehtype,
      vehCode: useFilters.vehCode,
      vehName: useFilters.vehName,
      vehRegNo: useFilters.vehRegNo,
    };

    const { data } = await axios.get(`${API_BASE}/list`, {
      params,
      headers: apiHeaders,
    });

    if (data?.success === false) {
      throw new Error(data?.message || "List failed");
    }

    const list = data?.rows || [];
    setRows(list);
    setFilteredRows(list);
    setCurrentPage(1);
    toast.success(`Found ${list.length} record(s)`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await fetchList(filters);
    } catch (err) {
      toast.error(err?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = async () => {
    const reset = {
      ...filters,
      branch: "",
      fromDate: "",
      toDate: "",
      vehCode: "",
      vehName: "",
      vehRegNo: "",
    };

    setFilters(reset);

    try {
      setLoading(true);
      await fetchList(reset);
      toast.info("Showing all records");
    } catch (err) {
      toast.error(err?.message || "View all failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
    }));
    toast.info("Filters cleared");
  };

  const handlePrintAll = () => {
    const params = new URLSearchParams();

    params.set("branchType", branchType || "HO");
    params.set("branch", filters.branch || "");
    params.set("fromDate", filters.fromDate || "");
    params.set("toDate", filters.toDate || "");
    params.set("vehtype", filters.vehtype || "O");
    params.set("vehCode", filters.vehCode || "");
    params.set("vehName", filters.vehName || "");
    params.set("vehRegNo", filters.vehRegNo || "");

    params.set("pageSize", String(itemsPerPage || 0));
    params.set("pageNo", String(currentPage || 1));
    params.set("totalRecords", String(filteredRows?.length || 0));

    window.open(
      `/vehicle/ProductionandDieselConsReport_Print?${params.toString()}`,
      "_blank",
      "width=1100,height=900,scrollbars=yes,resizable=yes"
    );
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchInit();
        await fetchList({
          ...filters,
          fromDate: filters.fromDate || fmtDDMMYYYY(new Date()),
          toDate: filters.toDate || fmtDDMMYYYY(new Date()),
        });
      } catch (err) {
        toast.error(err?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paginate = (n) => setCurrentPage(n);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION DIESEL CONSUMPTION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Production and Diesel Consumption Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {filteredRows.length} Records
              </span>

              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${showFilters ? " bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${showFilters ? " bg-gray-50" : ""}`
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={false}
                  >
                    {(branches || []).map((b) => (
                      <option key={b.value || "select"} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date (dd/MM/yyyy)</label>
                  <input
                    type="text"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date (dd/MM/yyyy)</label>
                  <input
                    type="text"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div className="flex items-end">
                  <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {financeYearStart ? (
                      <>
                        Financial Year Start: <b>{financeYearStart}</b>
                      </>
                    ) : (
                      <>Financial Year Start not set</>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg"
                    disabled={loading}
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg`}
                    disabled={loading}
                  >
                    <FaSearch /> Search
                  </button>

                  <button
                    onClick={handlePrintAll}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg`}
                  >
                    <FaPrint /> Print
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {filteredRows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center whitespace-nowrap">S.No</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Total Noof Trips</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Qty total(Tonnes)</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Qty Cumulative(Tonnes)</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Tonnes Per Trip</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Diesel Consumed (Ltr)</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Diesel Consumed Per Trip</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Diesel Consumed Per tonnes</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => (
                      <tr
                        key={`${r.fld_progressentrydate ?? idx}-${idx}`}
                        className={`${
                          isDark ? "bg-gray-800" : "bg-white"
                        } hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors`}
                      >
                        <td
                          className={`px-4 py-3 text-center text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.fld_progressentrydate)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.nooftrips)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.qtytotal)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.qtycumulative)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.tonnespertrip)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.dieselconsumedltr)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.dieselconsumedpertrip)}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toStr(r.dieselconsumedtonnes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {filteredRows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredRows.length)} of{" "}
                {filteredRows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}